import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

export type CultureTheme =
  | 'global'
  | 'african'
  | 'western'
  | 'eastern'
  | 'latin';

type CultureThemeCtx = {
  theme: CultureTheme;
  setTheme: (t: CultureTheme) => void;
  colors: Record<string, string>;
  gradients: Record<string, string>;
};

const Ctx = createContext<CultureThemeCtx | null>(null);
const KEY = 'backon.culture.theme';
const DEFAULT: CultureTheme = 'global';

// Culture-specific color palettes
const cultureColors = {
  global: {
    primary: 'var(--brand-business)',
    secondary: '#6366f1',
    accent: 'var(--accent)',
    warm: 'var(--warning)',
    cool: 'var(--brand-care)',
  },
  african: {
    primary: '#dc2626',
    secondary: '#ea580c',
    accent: '#ca8a04',
    warm: 'var(--warning)',
    cool: 'var(--green-600)',
  },
  western: {
    primary: 'var(--blue-900)',
    secondary: 'var(--purple-700)',
    accent: '#db2777',
    warm: '#dc2626',
    cool: 'var(--cyan-600)',
  },
  eastern: {
    primary: '#dc2626',
    secondary: '#ca8a04',
    accent: '#7c2d12',
    warm: '#ea580c',
    cool: '#16a34a',
  },
  latin: {
    primary: '#dc2626',
    secondary: '#ca8a04',
    accent: '#16a34a',
    warm: '#ea580c',
    cool: 'var(--cyan-600)',
  },
};

const cultureGradients = {
  global: {
    hero: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    card: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  },
  african: {
    hero: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    card: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
  },
  western: {
    hero: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    card: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
  },
  eastern: {
    hero: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)',
    card: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
  },
  latin: {
    hero: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    card: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
  },
};

export const CultureThemeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [theme, setThemeState] = useState<CultureTheme>(DEFAULT);

  useEffect(() => {
    try {
      const saved = window.localStorage.getItem(KEY) as CultureTheme | null;
      if (saved && saved in cultureColors) {
        setThemeState(saved);
      }
    } catch {}
  }, []);

  const setTheme = (t: CultureTheme) => {
    setThemeState(t);
    try {
      localStorage.setItem(KEY, t);
    } catch {}
  };

  const value = useMemo(
    () => ({
      theme,
      setTheme,
      colors: cultureColors[theme],
      gradients: cultureGradients[theme],
    }),
    [theme]
  );

  // Apply CSS custom properties for the current theme
  useEffect(() => {
    const root = document.documentElement;
    const colors = cultureColors[theme];

    root.style.setProperty('--culture-primary', colors.primary);
    root.style.setProperty('--culture-secondary', colors.secondary);
    root.style.setProperty('--culture-accent', colors.accent);
    root.style.setProperty('--culture-warm', colors.warm);
    root.style.setProperty('--culture-cool', colors.cool);
  }, [theme]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const useCultureTheme = () => {
  const ctx = useContext(Ctx);
  if (!ctx)
    throw new Error('useCultureTheme must be used within CultureThemeProvider');
  return ctx;
};
